import { useState } from "react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { useDraft } from "@/components/itsm-config/draft-context";
import { FieldImportDialog } from "@/components/itsm-config/field-import-dialog";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import {
  readStoredProviderConfig,
  useImportProviderFields,
  useJiraImportableFields,
  useMondayImportableColumns,
  type FieldImportRequest,
  type ImportProvider,
} from "@/hooks/use-field-import";
import { useJiraProjects } from "@/hooks/use-itsm-metadata";
import { apiErrorMessage } from "@/lib/api/error-message";
import {
  importedMappings,
  withImported,
  type FieldImportResult,
  type ImportTarget,
} from "@/lib/itsm-config/field-import";
import { patchBoardSync } from "@/lib/itsm-config/monday";
import type { ProviderConfig } from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
}

type MappingsPatch = (
  current: ProviderConfig,
  stored: ProviderConfig | null,
) => Partial<ProviderConfig>;

/**
 * Runs an import and folds what the backend saved into the editor. When the
 * answer is lost, the backend may have saved anyway; the editor then takes
 * the stored mappings, so a later Save cannot drop them.
 */
function useImportRunner(
  tenantId: string,
  credentialId: string,
  provider: ImportProvider,
  added: (result: FieldImportResult) => MappingsPatch,
  stored: MappingsPatch,
) {
  const { applySaved } = useDraft();
  const run = useImportProviderFields(tenantId, credentialId, provider);

  const onImport = async (
    request: FieldImportRequest,
  ): Promise<FieldImportResult | null> => {
    try {
      const result = await run.mutateAsync(request);
      applySaved((current) => added(result)(current, null));
      announce(result);
      return result;
    } catch (err) {
      toast.error(
        apiErrorMessage(
          err as AxiosError<{ detail?: unknown }>,
          "Import failed",
        ),
      );
      try {
        const saved = await readStoredProviderConfig(tenantId, credentialId);
        applySaved((current) => stored(current, saved));
      } catch {
        toast.error(
          "Could not re-read the saved mappings; reload the page before saving.",
        );
      }
      return null;
    }
  };
  return { onImport, importing: run.isPending };
}

function announce(result: FieldImportResult) {
  const created = result.imported.filter((r) => r.created).length;
  const skipped = result.skipped.length;
  const text =
    `${String(created)} field${created === 1 ? "" : "s"} created and paired` +
    (skipped ? `, ${String(skipped)} skipped` : "");
  if (created === 0 || skipped > 0) toast.warning(text);
  else toast.success(text);
}

/** Import a Jira project's custom fields into the config's field mappings. */
export function JiraFieldImport({ tenantId, credentialId }: Props) {
  const [open, setOpen] = useState(false);
  const [projectKey, setProjectKey] = useState("");
  const projects = useJiraProjects(open ? tenantId : undefined, credentialId);
  const fields = useJiraImportableFields(
    tenantId,
    credentialId,
    projectKey,
    open,
  );
  const { onImport, importing } = useImportRunner(
    tenantId,
    credentialId,
    "jira",
    (result) => (current) => ({
      field_mappings: withImported(
        current.field_mappings,
        importedMappings(result, projectKey),
      ),
    }),
    (_, stored) => ({ field_mappings: stored?.field_mappings ?? null }),
  );

  return (
    <FieldImportDialog
      // A new project is a new list: nothing picked from the last one stays.
      key={projectKey}
      tenantId={tenantId}
      noun="Jira fields"
      open={open}
      onOpenChange={setOpen}
      source={
        <MetadataSelect
          aria-label="Jira project"
          options={projects.data?.options ?? []}
          isLoading={projects.isLoading}
          value={projectKey}
          onChange={setProjectKey}
          placeholder="Jira project whose create screens to read..."
          allowCustom={false}
        />
      }
      sourceHint="Choose a project to list its custom fields."
      fields={projectKey ? fields.data : undefined}
      isLoading={Boolean(projectKey) && fields.isLoading}
      loadError={fields.isError}
      importing={importing}
      onImport={(target: ImportTarget, deskId, ids) =>
        onImport({ target, desk_id: deskId, project_key: projectKey, ids })
      }
    />
  );
}

/** Import one monday board's columns into that board's field mappings. */
export function MondayColumnImport({
  tenantId,
  credentialId,
  boardId,
}: Props & { boardId: string }) {
  const [open, setOpen] = useState(false);
  const columns = useMondayImportableColumns(
    tenantId,
    credentialId,
    boardId,
    open,
  );
  const { onImport, importing } = useImportRunner(
    tenantId,
    credentialId,
    "monday",
    (result) => (current) =>
      patchBoardSync(current, boardId, {
        field_mappings: withImported(
          // eslint-disable-next-line security/detect-object-injection -- boardId is a monday board id from our own config
          current.board_sync?.[boardId]?.field_mappings,
          importedMappings(result),
        ),
      }),
    (current, stored) =>
      patchBoardSync(current, boardId, {
        // eslint-disable-next-line security/detect-object-injection -- boardId is a monday board id from our own config
        field_mappings: stored?.board_sync?.[boardId]?.field_mappings ?? null,
      }),
  );

  return (
    <FieldImportDialog
      tenantId={tenantId}
      noun="board columns"
      open={open}
      onOpenChange={setOpen}
      fields={columns.data}
      isLoading={columns.isLoading}
      loadError={columns.isError}
      importing={importing}
      onImport={(target: ImportTarget, deskId, ids) =>
        onImport({ target, desk_id: deskId, board_id: boardId, ids })
      }
    />
  );
}
